/**
 * API Response Schemas
 *
 * Live response shapes actually consumed by the app. DEV2-1271 removed the
 * unused generic API-envelope scaffolding (status/error/pagination/websocket/
 * health-check/rate-limit schemas) that was never wired to a real response —
 * only the pieces below feed a live service, hook or component.
 */

import { z } from 'zod';

// API response status
const apiStatusSchema = z.enum(['success', 'error', 'loading']);

// Base API response schema
const baseApiResponseSchema = z.object({
  status: apiStatusSchema,
  message: z.string().optional(),
  timestamp: z.number(),
  requestId: z.string().optional(),
});

// Success response schema
const successResponseSchema = <T extends z.ZodTypeAny>(
  dataSchema: T
): z.ZodObject<
  z.ZodRawShape & {
    status: z.ZodLiteral<'success'>;
    data: T;
  }
> =>
  baseApiResponseSchema.extend({
    status: z.literal('success'),
    data: dataSchema,
  });

// Error detail schema
const errorDetailSchema = z.object({
  field: z.string().optional(),
  code: z.string(),
  message: z.string(),
  value: z.unknown().optional(),
});

// Bulk operation response schema
const bulkOperationResultSchema = z.object({
  id: z.string(),
  success: z.boolean(),
  error: errorDetailSchema.optional(),
});

export const bulkOperationResponseSchema = successResponseSchema(
  z.object({
    totalRequested: z.number(),
    totalSucceeded: z.number(),
    totalFailed: z.number(),
    results: z.array(bulkOperationResultSchema),
  })
);

export type BulkOperationResponse = z.infer<typeof bulkOperationResponseSchema>;

// ============================================================================
// Backend Response Format (Standardized API Response)
// ============================================================================

/**
 * Backend meta information included in all responses
 */
const apiMetaSchema = z.object({
  requestId: z.string(),
  timestamp: z.string().datetime(),
  version: z.string(),
  duration: z.number().optional(),
});

/**
 * HATEOAS links for API navigation and discoverability.
 * Not validated at runtime (no live caller ever parsed against this shape) —
 * kept as a plain type rather than an unused Zod schema.
 */
type ApiLinks = {
  self: string;
  collection?: string;
  first?: string;
  prev?: string;
  next?: string;
  last?: string;
  related?: Record<string, string>;
  documentation?: string;
};

/**
 * Backend pagination metadata (separate from flat pagination).
 * Not validated at runtime — see ApiLinks above.
 */
type ApiPagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
};

type ApiMeta = z.infer<typeof apiMetaSchema>;

export type BackendSuccessResponse<T> = {
  data: T;
  meta: ApiMeta;
  links: ApiLinks;
  pagination?: ApiPagination;
};

/**
 * Backend error response format
 * All error responses follow this structure
 */
export const backendErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    statusCode: z.number(),
    details: z.array(errorDetailSchema).optional(),
  }),
  meta: apiMetaSchema,
  links: z.object({
    self: z.string(),
    documentation: z.string().optional(),
  }),
});

export type BackendErrorResponse = z.infer<typeof backendErrorResponseSchema>;

// ============================================================================
// Delete Blocker Types (409 Conflict pre-deletion validation)
// ============================================================================

/**
 * A blocker returned by the backend when a soft-delete is blocked by dependencies.
 * The frontend uses these to display consequences in the ConfirmDeleteDialog before
 * allowing the user to force-delete with ?force=true.
 */
export interface DeleteBlocker {
  /** Type of blocker: 'tag' | 'recentPositions' | 'assets' | 'zones' | 'tags' | 'userAccess' */
  type: string;
  /** ID of the blocking entity (if applicable) */
  id?: string;
  /** Human-readable name of the blocking entity (if applicable) */
  name?: string;
  /** Count of blocking entities (if applicable) */
  count?: number;
}

/** Shape of the 409 error details returned by the backend */
export interface DeleteConflictDetails {
  blockers: DeleteBlocker[];
}
